import type { ApprovalRecordType } from './approvals.js';
import { DomainError } from './errors.js';

/**
 * Onayda parolanın yeniden istenmesi (sahip kararı 2026-10-17; ADR 0075, ADR 0056'yı değiştirir).
 * Oturum yeterlidir: hesabına giriş yapmış kişi yetkisi olan bütün onay ve imzaları açık bir onay
 * adımıyla (iletişim kutusunda düğme) verir. Şirket ayarı "Onayda parolayı her yerde iste"
 * (`reauthAlways`) açıkken bütün türlerde parola yeniden istenir.
 *
 * NR496 yalnız girişte kişiye özel kimlik doğrulama ister [NR496 4.2.1]; onay anında yeniden
 * doğrulama istemez. Giriş parolası ve TOTP bu kuralın dışındadır.
 *
 * Tür listesi, onay ve imza noktalarının tek listesidir: bilinmeyen tür (yazım hatası) en sıkı
 * okunur, parola ister. Tür eklenince tablo testi kırılır.
 */
export const REAUTH_KINDS = [
  // Onay matrisinin türleri (`APPROVAL_RECORD_TYPES`; döngüsel içe aktarma olmasın diye açık liste,
  // eşitliği test denetler)
  'rest_hours_month',
  'voyage_report',
  'requisition',
  'document_revision',
  'ism_form',
  'vessel_document_revision',
  'pms_work_record',
  'postponement_request',
  'maintenance_interval_change',
  'defect_report',
  'breakdown_report',
  'hse_non_conformity',
  'hse_deficiency',
  'hse_near_miss',
  'hse_accident',
  'daily_report',
  'risk_assessment',
  'ctm_request',
  'fuel_opening_balance',
  'fuel_bunkering',
  'fuel_movement',
  'fuel_adjustment',
  // Kaptanın şirket revizyonunu gemi adına teyidi (ADR 0028 #26, #27)
  'document_acknowledgement',
  // Kişisel "Read & Understood": şirket ve gemi belgesi (ADR 0028 #29, ADR 0038 §12)
  'document_read',
  // Ofisin açtığı şirket uygunsuzluğunun kapanışı ve yeniden açılması (ADR 0062 §2.5)
  'company_nonconformity',
  // Kaptanın gemi saati değişikliği onayı (ADR 0065 §2)
  'ship_clock_change',
  // Satın alma siparişinin onayı ya da iadesi (ADR 0040 #177)
  'purchase_order',
  // Bordro döneminin kapanışı ve yeniden açılması (ADR 0061)
  'payroll_period',
  // Şirket RA kütüphanesinin gönderimi, incelemesi, kabulü, onayı ve yayımı (ADR 0087 §3)
  'ra_library',
  // Kaptanın ya da yetkili zabitin yeni şirket RA sürümünü okundu onayı (ADR 0088 §3)
  'ra_acknowledgement',
  // Operasyonel doğrulamanın yetki kararı: Authorize, Return, Reject (ADR 0089 §4)
  'ra_operation',
  // Kaptanın CTM teyidi ve kasa sayımı (ay sonu, devir, devralma) imzası (ADR 0093 §3, §5)
  'ctm_receipt',
  'cash_reconciliation',
  // Ofisin talep revizyonu: onaylı listeyi gemiye gönderir (ADR 0111 §A)
  'requisition_revision',
  // Tedarikçi banka hesabı değişikliğinin ofiste doğrulanması ve onayı (dört göz; ADR 0114 §4)
  'supplier_bank_review',
  // Tedarikçi faturasının incelemesi ve ödemenin ikinci kişi onayı (ADR 0119 §3, §4)
  'supplier_invoice_review',
  'supplier_payment_approval',
  // Yakıt dönem kilidi ve yeniden açma: şirket onaylayıcısının kararı (ADR 0117 §12, Ek Y7)
  'fuel_period_lock',
  // Tatbikat (ADR 0128 Faz 2 Ek): kaptanın erteleme ve kayıtlı uygulama kararı, ofisin gemiye özel
  // varyant kararı
  'drill_decision',
  'drill_variant_decision',
  // Pre-Docking (ADR 0129 Ek Faz 3): gemide teknik inceleme ve ofise gönderim (Master, Chief
  // Engineer), ofiste işin onayı, iadesi ya da reddi (Superintendent)
  'drydock_review',
  'drydock_job_decision',
  // Pre-Docking Faz 4 (ADR 0129 Ek Faz 4): gemide iş, bulgu ve proje kapanışı (Master, Chief
  // Engineer), ofiste tamamlanan işin ve kritik bulgu kapanışının doğrulaması (Superintendent)
  'drydock_close',
  'drydock_verification',
] as const;
export type ReauthKind =
  | ApprovalRecordType
  | 'document_acknowledgement'
  | 'document_read'
  | 'company_nonconformity'
  | 'ship_clock_change'
  | 'purchase_order'
  | 'payroll_period'
  | 'ra_library'
  | 'ra_acknowledgement'
  | 'ra_operation'
  | 'ctm_receipt'
  | 'cash_reconciliation'
  | 'requisition_revision'
  | 'supplier_bank_review'
  | 'supplier_invoice_review'
  | 'supplier_payment_approval'
  | 'fuel_period_lock'
  | 'drill_decision'
  | 'drill_variant_decision'
  | 'drydock_review'
  | 'drydock_job_decision'
  | 'drydock_close'
  | 'drydock_verification';

/** Şirketin onay ayarı (`company_settings.approval_reauth_always`; ADR 0056 §4). */
export interface ApprovalReauthPolicy {
  /** "Onayda parolayı her yerde iste": açıkken bütün türler zorunludur. */
  reauthAlways: boolean;
}

/** Ayarın varsayılanı (kapalı: oturum yeter). */
export const APPROVAL_REAUTH_DEFAULT: ApprovalReauthPolicy = { reauthAlways: false };
/** Politika bilinmiyorsa en sıkı okuma: her tür zorunlu. */
export const APPROVAL_REAUTH_STRICT: ApprovalReauthPolicy = { reauthAlways: true };

const KINDS: ReadonlySet<string> = new Set(REAUTH_KINDS);

/** Tür onay ve imza listesinde mi. */
export function isReauthKind(kind: string): kind is ReauthKind {
  return KINDS.has(kind);
}

/**
 * Bu türde onay anında parola gerekli mi: yalnız şirket ayarı açıkken. Politika verilmezse ya da
 * tür listede yoksa en sıkı okuma (zorunlu): yazım hatası sessizce parolasız bir türe dönüşmez.
 */
export function isReauthRequired(kind: string, policy: ApprovalReauthPolicy | undefined): boolean {
  return (policy ?? APPROVAL_REAUTH_STRICT).reauthAlways || !isReauthKind(kind);
}

/** Parola gerekliyse ve yeniden doğrulanmadıysa `REAUTHENTICATION_REQUIRED`. */
export function assertReauthenticated(
  kind: string,
  reauthenticated: boolean,
  policy: ApprovalReauthPolicy | undefined,
): void {
  if (!reauthenticated && isReauthRequired(kind, policy)) {
    throw new DomainError('REAUTHENTICATION_REQUIRED');
  }
}
